import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface Hq {
  id: string;
  name: string;
  zone_ids: string[];
  zones: Array<{ id: string; name: string }>;
  state: string;
  city: string;
  status: string;
  /** Real HQ code from the source system. */
  hq_code?: string;
  /** Name of the manager / parent HQ. */
  manager_hq?: string;
  /**
   * Area names served by this HQ.
   * Always an array — never null. Typical HQs carry 5–32 entries.
   */
  area_names: string[];
  created_at: string;
  updated_at: string;
}

export interface HqCreateRequest {
  name: string;
  zone_ids: string[];
  state: string;
  city: string;
  status: string;
  /**
   * Area names for this HQ.
   * The API trims, de-duplicates (case-insensitive) and sorts — the returned
   * order may differ from what was sent.
   */
  area_names?: string[];
}

export interface HqUpdateRequest {
  name?: string;
  zone_ids?: string[];
  state?: string;
  city?: string;
  status?: string;
  /**
   * Full area-names list for this HQ.
   * PATCH *replaces* the whole list — send the complete set, not a delta.
   */
  area_names?: string[];
}

export interface HqListResponse {
  headquarters: Hq[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_count: number;
    limit: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class HqApiService {
  private baseUrl = `${environment.apiUrl}/headquarters`;

  constructor(private http: HttpClient) {}

  getHqs(page: number = 1, limit: number = 10, search: string = ''): Observable<HqListResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    
    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<HqListResponse>(`${this.baseUrl}/`, { params });
  }

  getHqById(id: string): Observable<Hq> {
    return this.http.get<Hq>(`${this.baseUrl}/${id}`);
  }

  createHq(data: HqCreateRequest): Observable<Hq> {
    return this.http.post<Hq>(`${this.baseUrl}/`, data);
  }

  updateHq(id: string, data: HqUpdateRequest): Observable<Hq> {
    return this.http.patch<Hq>(`${this.baseUrl}/${id}`, data);
  }

  deleteHq(id: string): Observable<string> {
    return this.http.delete<string>(`${this.baseUrl}/${id}`);
  }
}
